import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, CheckCircle, ArrowRight, ArrowLeft, Loader2, Award, BarChart3 } from 'lucide-react';
import { selfAssessmentApi, SelfAssessmentQuestion } from '../api/selfAssessment';

type QuizState = 'intro' | 'quiz' | 'submitting' | 'results';

export const SelfAssessment: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<QuizState>('intro');
  const [questions, setQuestions] = useState<SelfAssessmentQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [attemptId, setAttemptId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [results, setResults] = useState<{
    score: number;
    total: number;
    percentage: number;
    byLabel: Record<string, { correct: number; total: number; percentage: number }>;
  } | null>(null);

  const currentQuestion = questions[currentQuestionIndex];
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const data = await selfAssessmentApi.getQuestions();
      setQuestions(data);
    } catch (err: any) {
      setError('Kon vragen niet laden. Probeer het later opnieuw.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const startAssessment = async () => {
    try {
      setLoading(true);
      
      // Generate a temporary user ID for anonymous users
      const tempUserId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const attempt = await selfAssessmentApi.createAttempt(tempUserId);
      setAttemptId(attempt.id);
      setState('quiz');
    } catch (err: any) {
      setError('Kon zelftest niet starten. Probeer het later opnieuw.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const selectAnswer = (answer: string) => {
    setAnswers({
      ...answers,
      [currentQuestion.id]: answer
    });
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const submitAssessment = async () => {
    try {
      setState('submitting');
      const result = await selfAssessmentApi.submitAttempt(attemptId, answers, questions);
      
      // Calculate results
      const total = questions.length;
      const score = result.score || 0;
      const percentage = Math.round((score / total) * 100);

      const byLabel: Record<string, { correct: number; total: number; percentage: number }> = {};
      Object.entries(result.results_by_label).forEach(([label, stats]) => {
        byLabel[label] = {
          ...stats,
          percentage: Math.round((stats.correct / stats.total) * 100)
        };
      });

      setResults({
        score,
        total,
        percentage,
        byLabel
      });

      setState('results');
    } catch (err: any) {
      setError('Kon resultaten niet opslaan. Probeer het opnieuw.');
      setState('quiz');
      console.error(err);
    }
  };

  const restartAssessment = () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setAttemptId('');
    setResults(null);
    setState('intro');
  };

  if (loading && questions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Vragen laden...</p>
        </div>
      </div>
    );
  }

  // Intro Screen
  if (state === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Brain className="w-10 h-10 text-blue-600" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                AI Geletterdheid Zelftest
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Test je kennis van Artificial Intelligence en ontdek waar je staat
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            <div className="space-y-6 mb-8">
              <div className="bg-blue-50 rounded-2xl p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Wat kun je verwachten?
                </h2>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>20 vragen</strong> over verschillende aspecten van AI
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>5 categorieën:</strong> Basis Kennis, Praktische Toepassing, Ethiek & Privacy, Toekomst & Trends, Data & Training
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>15-20 minuten</strong> om de test te voltooien
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      Gedetailleerde <strong>uitleg bij elk antwoord</strong>
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      Persoonlijk <strong>rapport per categorie</strong>
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <Award className="w-5 h-5 text-amber-600 mr-2" />
                  Let op
                </h3>
                <p className="text-gray-700">
                  Deze test is bedoeld om je kennis te meten en leerpunten te identificeren. 
                  Het is geen officiële certificering, maar een nuttig hulpmiddel voor je leerproces.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={startAssessment}
                disabled={loading}
                className="flex-1 inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white rounded-2xl font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Laden...
                  </>
                ) : (
                  <>
                    Start Zelftest
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-8 py-4 bg-white text-gray-700 border-2 border-gray-200 rounded-2xl font-semibold hover:border-blue-300 hover:bg-blue-50 transition-all duration-300"
              >
                Terug naar Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Quiz Screen
  if (state === 'quiz' && currentQuestion) {
    const hasAnsweredCurrent = !!answers[currentQuestion.id];
    const allQuestionsAnswered = questions.every(q => !!answers[q.id]);

    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Vraag {currentQuestionIndex + 1} van {questions.length}
              </span>
              <span className="text-sm font-medium text-gray-700">
                {Math.round(progress)}% voltooid
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
            <div className="mb-4">
              <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium mb-3">
                {currentQuestion.label}
              </span>
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {currentQuestion.question}
              </h2>
            </div>

            {/* Answer Options */}
            <div className="space-y-2">
              {['A', 'B', 'C', 'D'].map((option) => {
                const isSelected = answers[currentQuestion.id] === option;
                const choiceKey = `choice_${option.toLowerCase()}` as keyof SelfAssessmentQuestion;
                
                return (
                  <button
                    key={option}
                    onClick={() => selectAnswer(option)}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 ${
                      isSelected
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5 ${
                        isSelected
                          ? 'border-blue-600 bg-blue-600'
                          : 'border-gray-300'
                      }`}>
                        {isSelected && (
                          <CheckCircle className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 mb-1 text-sm">
                          {option}.
                        </div>
                        <div className="text-gray-700 text-sm">
                          {currentQuestion[choiceKey] as string}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={goToPreviousQuestion}
              disabled={currentQuestionIndex === 0}
              className="inline-flex items-center px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Vorige
            </button>

            {currentQuestionIndex === questions.length - 1 ? (
              <button
                onClick={submitAssessment}
                disabled={!allQuestionsAnswered}
                className="inline-flex items-center px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {allQuestionsAnswered ? 'Resultaten Bekijken' : 'Beantwoord alle vragen'}
              </button>
            ) : (
              <button
                onClick={goToNextQuestion}
                disabled={!hasAnsweredCurrent}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Volgende
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Submitting Screen
  if (state === 'submitting') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-xl text-gray-700 font-medium">Resultaten berekenen...</p>
        </div>
      </div>
    );
  }

  // Results Screen
  if (state === 'results' && results) {
    const getScoreColor = (percentage: number) => {
      if (percentage >= 80) return 'text-green-600';
      if (percentage >= 60) return 'text-blue-600';
      if (percentage >= 40) return 'text-amber-600';
      return 'text-red-600';
    };

    const getScoreBgColor = (percentage: number) => {
      if (percentage >= 80) return 'bg-green-100';
      if (percentage >= 60) return 'bg-blue-100';
      if (percentage >= 40) return 'bg-amber-100';
      return 'bg-red-100';
    };

    const getScoreMessage = (percentage: number) => {
      if (percentage >= 80) return { title: 'Uitstekend!', message: 'Je hebt een uitstekende kennis van AI' };
      if (percentage >= 60) return { title: 'Goed gedaan!', message: 'Je hebt een goede basiskennis van AI' };
      if (percentage >= 40) return { title: 'Redelijk', message: 'Er is ruimte voor verbetering' };
      return { title: 'Blijf leren', message: 'Je hebt nog veel te ontdekken over AI' };
    };

    const scoreInfo = getScoreMessage(results.percentage);

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            {/* Overall Score */}
            <div className="text-center mb-12">
              <div className={`w-32 h-32 ${getScoreBgColor(results.percentage)} rounded-full flex items-center justify-center mx-auto mb-6`}>
                <div className={`text-5xl font-bold ${getScoreColor(results.percentage)}`}>
                  {results.percentage}%
                </div>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {scoreInfo.title}
              </h1>
              <p className="text-xl text-gray-600 mb-4">
                {scoreInfo.message}
              </p>
              <p className="text-gray-700">
                Je hebt <strong>{results.score}</strong> van de <strong>{results.total}</strong> vragen correct beantwoord
              </p>
            </div>

            {/* Results by Category */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <BarChart3 className="w-6 h-6 mr-3 text-blue-600" />
                Resultaten per Categorie
              </h2>
              <div className="space-y-4">
                {Object.entries(results.byLabel)
                  .sort((a, b) => b[1].percentage - a[1].percentage)
                  .map(([label, stats]) => (
                    <div key={label} className="bg-gray-50 rounded-2xl p-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-gray-900">{label}</h3>
                        <span className={`text-lg font-bold ${getScoreColor(stats.percentage)}`}>
                          {stats.percentage}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                        <span>{stats.correct} van {stats.total} correct</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-500 ${
                            stats.percentage >= 80 ? 'bg-green-500' :
                            stats.percentage >= 60 ? 'bg-blue-500' :
                            stats.percentage >= 40 ? 'bg-amber-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${stats.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-4">
              <button
                onClick={() => navigate('/self-assessment/review/' + attemptId)}
                className="w-full inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white rounded-2xl font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Bekijk Gedetailleerde Antwoorden
              </button>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={restartAssessment}
                  className="flex-1 px-8 py-4 bg-white text-gray-700 border-2 border-gray-200 rounded-2xl font-semibold hover:border-blue-300 hover:bg-blue-50 transition-all duration-300"
                >
                  Opnieuw Doen
                </button>
                <button
                  onClick={() => navigate('/topics')}
                  className="flex-1 px-8 py-4 bg-white text-gray-700 border-2 border-gray-200 rounded-2xl font-semibold hover:border-blue-300 hover:bg-blue-50 transition-all duration-300"
                >
                  Bekijk Trainingen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

